import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { categories } from '@/mocks/categories';
import { articles } from '@/mocks/articles';
import { pillarArticles, type PillarArticle } from '@/mocks/pillarArticles';
import { useSeo } from '@/hooks/useSeo';
import { useJsonLd } from '@/hooks/useJsonLd';
import { SITE_NAME, SITE_FAVICON, siteOrigin } from '@/lib/seo';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const { t, i18n } = useTranslation();
  const [currentLang, setCurrentLang] = useState(i18n.language || 'en');

  useEffect(() => {
    setCurrentLang(i18n.language);
  }, [i18n.language]);

  const handleLangChange = (lang: string) => {
    i18n.changeLanguage(lang);
  };

  const meta = articles.find((a) => a.slug === slug);

  const origin = siteOrigin();
  const resolvedSlug = slug || '';

  useSeo({
    title: meta ? meta.title : SITE_NAME,
    description: meta
      ? meta.excerpt
      : 'AllChinaBuy spreadsheet resources, buying guides and product information.',
    keywords: meta
      ? `AllChinaBuy ${meta.category}, AllChinaBuy spreadsheet, AllChinaBuy buying guide`
      : 'AllChinaBuy spreadsheet',
    path: `/articles/${resolvedSlug}/`,
    type: 'article',
    image: meta ? meta.image : undefined,
    article: meta
      ? { publishedTime: meta.date, modifiedTime: meta.date, section: meta.category }
      : undefined,
  });

  useJsonLd(
    meta
      ? [
          {
            id: 'article',
            data: {
              '@context': 'https://schema.org',
              '@type': 'BlogPosting',
              headline: meta.title,
              description: meta.excerpt,
              image: meta.image,
              datePublished: meta.date,
              dateModified: meta.date,
              author: { '@type': 'Organization', name: SITE_NAME },
              publisher: {
                '@type': 'Organization',
                name: SITE_NAME,
                logo: { '@type': 'ImageObject', url: SITE_FAVICON },
              },
              mainEntityOfPage: origin
                ? `${origin}/articles/${resolvedSlug}/`
                : `/articles/${resolvedSlug}/`,
            },
          },
        ]
      : [],
  );

  if (!meta) {
    return <div className="min-h-screen bg-background-50 pt-24 text-center text-foreground-700">Article not found</div>;
  }

  const isCompleteGuide = slug === 'allchinabuy-spreadsheet-complete-guide';
  const pillar = pillarArticles.find((a) => a.slug === slug);

  const relatedArticles = articles.filter((a) => a.slug !== slug).slice(0, 6);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar currentLang={currentLang} onLangChange={handleLangChange} />

      {/* Article Header */}
      <section className="relative pt-24 pb-12 md:pt-32 md:pb-20">
        <div className="absolute inset-0 overflow-hidden">
          <img
            src={meta.image}
            alt={meta.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-background-50" />
        </div>

        <div className="relative z-10 w-full px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-white/70 mb-6 flex-wrap">
            <a href="/" className="hover:text-white transition-colors">{t('nav.home')}</a>
            <i className="ri-arrow-right-s-line text-xs" />
            <a href="/" className="hover:text-white transition-colors">{t('nav.articles')}</a>
            <i className="ri-arrow-right-s-line text-xs" />
            <span className="text-white/90">{meta.title}</span>
          </nav>

          <span className="inline-block px-3 py-1 rounded-full bg-primary-500/90 text-white text-xs font-medium mb-4">
            {meta.category}
          </span>
          <h1 className="text-3xl md:text-5xl font-bold text-white leading-tight mb-4">
            {meta.title}
          </h1>
          <div className="flex items-center gap-4 text-sm text-white/80">
            <span>{meta.date}</span>
            <span className="w-1 h-1 rounded-full bg-white/50" />
            <span>{meta.readTime}</span>
          </div>
        </div>
      </section>

      {/* Article Body */}
      <article className="w-full px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto pb-20">
        <div className="prose prose-lg max-w-none">
          <div className="text-foreground-700 leading-relaxed space-y-6">
            {isCompleteGuide ? <CompleteGuideContent /> : pillar ? <PillarContent pillar={pillar} /> : null}
          </div>
        </div>

        {/* Related Articles */}
        <div className="mt-16 pt-10 border-t border-background-200">
          <h3 className="text-xl font-bold text-foreground-950 mb-6">Related Articles</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {relatedArticles.map((a) => (
              <a
                key={a.id}
                href={`/articles/${a.slug}/`}
                className="group flex flex-col rounded-xl bg-white border border-background-200 hover:border-primary-300 hover:shadow-md transition-all overflow-hidden"
              >
                <div className="h-28 overflow-hidden">
                  <img src={a.image} alt={a.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                </div>
                <div className="p-4 flex flex-col flex-1">
                  <span className="text-xs text-foreground-400 mb-2">{a.category}</span>
                  <h4 className="text-sm font-semibold text-foreground-900 leading-snug group-hover:text-primary-600 transition-colors">
                    {a.title}
                  </h4>
                </div>
              </a>
            ))}
          </div>
        </div>

        {/* Related Categories */}
        <div className="mt-12">
          <h3 className="text-xl font-bold text-foreground-950 mb-6">Related Product Categories</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {categories.slice(0, 8).map((cat) => (
              <a
                key={cat.slug}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="group flex items-center gap-3 p-3 rounded-lg bg-white border border-background-200 hover:border-primary-300 hover:shadow-sm transition-all"
              >
                <span className="w-10 h-10 rounded-md overflow-hidden flex-shrink-0 bg-background-100">
                  <img src={cat.image} alt={cat.name} className="w-full h-full object-cover" />
                </span>
                <span className="text-sm font-medium text-foreground-700 group-hover:text-primary-600 transition-colors">
                  {cat.name}
                </span>
              </a>
            ))}
          </div>
        </div>

        {/* Back to Home CTA */}
        <div className="mt-12 text-center">
          <a
            href="/"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-white font-medium hover:bg-primary-600 transition-colors"
          >
            <i className="ri-arrow-left-line" />
            Back to Home
          </a>
        </div>

        {/* FAQ Section */}
        <div className="mt-16">
          <FAQSection />
        </div>
      </article>

      <Footer />
    </div>
  );
}

function PillarContent({ pillar }: { pillar: PillarArticle }) {
  return (
    <>
      <p className="text-xl text-foreground-800 font-medium leading-relaxed">
        {pillar.intro}
      </p>
      {pillar.sections.map((s, idx) => (
        <div key={idx}>
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mt-10 mb-4">
            {s.heading}
          </h2>
          {s.paragraphs.map((p, j) => (
            <p key={j} className="mb-4">
              {p}
            </p>
          ))}
          {s.bullets && (
            <ul className="list-disc list-inside space-y-2 ml-4 my-4">
              {s.bullets.map((b, k) => (
                <li key={k}>{b}</li>
              ))}
            </ul>
          )}
        </div>
      ))}
      <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mt-10 mb-4">
        Conclusion
      </h2>
      <p>
        {pillar.conclusion}
      </p>
    </>
  );
}

function CompleteGuideContent() {
  return (
    <>
      <p className="text-xl text-foreground-800 font-medium leading-relaxed">
        Navigating the world of overseas purchasing can feel overwhelming, especially when you are dealing with thousands of products, hundreds of sellers, and varying quality standards. The <strong>AllChinaBuy spreadsheet</strong> has emerged as one of the most practical tools for buyers who want organized access to product catalogs, seller information, and practical buying guides. This comprehensive guide will walk you through everything you need to know to use the AllChinaBuy spreadsheet effectively in 2025.
      </p>

      <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mt-10 mb-4">
        What Is the AllChinaBuy Spreadsheet?
      </h2>
      <p>
        The <a href="/" className="text-primary-600 font-medium hover:underline">AllChinaBuy spreadsheet</a> is an independently curated resource that organizes product listings from the AllChinaBuy purchasing platform into clear, searchable categories. Think of it as your shopping roadmap: instead of browsing endlessly through unstructured pages, you get a neatly formatted collection of products, complete with category filters, seller notes, and practical buying advice.
      </p>
      <p>
        Unlike generic product directories, this spreadsheet focuses on helping buyers make informed decisions. Each entry typically includes product category, approximate price ranges, seller reliability indicators, and quality control recommendations. This structured approach saves hours of research time and reduces the risk of disappointing purchases.
      </p>

      <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mt-10 mb-4">
        How to Use the AllChinaBuy Spreadsheet Effectively
      </h2>
      <p>
        Getting started with the spreadsheet is straightforward, but using it strategically requires a bit of know-how. Here is a step-by-step approach to maximize your shopping efficiency:
      </p>

      <ol className="list-decimal list-inside space-y-3 ml-4">
        <li>
          <strong>Start with your category:</strong> The spreadsheet is organized into product categories such as <a href="https://kaspreadsheet.com/category-911-Shoes.html" target="_blank" rel="noopener nofollow" className="text-primary-600 hover:underline">Shoes</a>, <a href="https://kaspreadsheet.com/search-912/name/Shoes.html" target="_blank" rel="noopener nofollow" className="text-primary-600 hover:underline">Hoodies and Sweaters</a>, <a href="https://kaspreadsheet.com/search-913/name/Shoes.html" target="_blank" rel="noopener nofollow" className="text-primary-600 hover:underline">T-Shirts</a>, and <a href="https://kaspreadsheet.com/search-914/name/Shoes.html" target="_blank" rel="noopener nofollow" className="text-primary-600 hover:underline">Jackets</a>. Begin by navigating to the section that matches what you are looking for.
        </li>
        <li>
          <strong>Compare sellers:</strong> Not all sellers offer the same quality or service level. The spreadsheet includes seller notes and reliability indicators. Take a moment to compare ratings and recent buyer feedback before making a decision.
        </li>
        <li>
          <strong>Check QC requirements:</strong> Quality Control is essential when buying remotely. The spreadsheet highlights which products and sellers benefit most from QC photo requests. Always opt for QC inspection when available, especially for higher-value items.
        </li>
        <li>
          <strong>Calculate total costs:</strong> Product price is only part of the equation. Factor in agent fees, domestic shipping, international shipping, and potential customs duties. The spreadsheet helps you estimate these costs before committing to a purchase.
        </li>
        <li>
          <strong>Use the search function:</strong> If you are looking for something specific, use the search or filter features to narrow down results quickly. This is especially useful when the spreadsheet contains hundreds of entries.
        </li>
      </ol>

      <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mt-10 mb-4">
        Understanding Product Categories
      </h2>
      <p>
        The strength of the AllChinaBuy spreadsheet lies in its thoughtful categorization. Here is what you can expect across the major product sections:
      </p>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-6">
        {[
          { name: 'Shoes', desc: 'Sneakers, boots, sandals, and formal footwear across multiple brands and styles.' },
          { name: 'Hoodies & Sweaters', desc: 'Streetwear and casual knitwear including branded and unbranded options.' },
          { name: 'T-Shirts', desc: 'Basic tees, graphic shirts, and designer-inspired tops in various materials.' },
          { name: 'Jackets', desc: 'Outerwear ranging from lightweight windbreakers to heavy winter coats.' },
          { name: 'Pants & Shorts', desc: 'Denim, cargo pants, joggers, athletic shorts, and casual trousers.' },
          { name: 'Accessories', desc: 'Watches, sunglasses, jewelry, belts, and other fashion add-ons.' },
          { name: 'Bags', desc: 'Backpacks, handbags, crossbody bags, and travel luggage options.' },
          { name: 'Perfumes', desc: 'Fragrance alternatives and inspired scents in various bottle sizes.' },
        ].map((item) => (
          <div key={item.name} className="p-4 rounded-lg bg-background-50 border border-background-200">
            <h4 className="font-semibold text-foreground-900 mb-1">{item.name}</h4>
            <p className="text-sm text-foreground-600">{item.desc}</p>
          </div>
        ))}
      </div>

      <p>
        Each category is maintained and updated to reflect current product availability and popular trends. By focusing on the categories that interest you most, you can shop more efficiently and avoid distraction from irrelevant listings.
      </p>

      <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mt-10 mb-4">
        Seller Verification and Trust
      </h2>
      <p>
        One of the biggest concerns when buying through purchasing agents is seller reliability. The AllChinaBuy spreadsheet addresses this by including seller indicators based on community feedback, transaction history, and return rates. While no system is perfect, these indicators provide a useful starting point for risk assessment.
      </p>
      <p>
        Here are practical tips for verifying sellers independently:
      </p>
      <ul className="list-disc list-inside space-y-2 ml-4">
        <li>Check recent reviews on the AllChinaBuy platform for the specific seller.</li>
        <li>Look for sellers with consistent QC photo quality and fast warehouse processing times.</li>
        <li>Join community forums and Reddit discussions to see real buyer experiences.</li>
        <li>Start with smaller test orders before making large purchases from a new seller.</li>
        <li>Avoid sellers with multiple recent complaints about wrong items or poor packaging.</li>
      </ul>

      <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mt-10 mb-4">
        Quality Control: Why It Matters
      </h2>
      <p>
        Quality Control (QC) is the process of inspecting your items at the warehouse before they are shipped internationally. This step is your safety net. It allows you to spot defects, wrong colors, incorrect sizes, or damaged items while the goods are still in China, where returns are feasible.
      </p>
      <p>
        The spreadsheet highlights which product categories and price points benefit most from QC. As a general rule, always request QC photos for items over $50, branded goods, electronics, and anything with precise sizing requirements. The small fee for QC is almost always worth the peace of mind it provides.
      </p>

      <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mt-10 mb-4">
        Shipping Methods and Cost Optimization
      </h2>
      <p>
        Shipping represents a significant portion of your total cost when buying through agents. The AllChinaBuy spreadsheet helps you plan by listing typical shipping weights and suggesting cost-effective methods for different product types. Common shipping options include:
      </p>

      <div className="overflow-x-auto my-6">
        <table className="w-full text-sm border border-background-200 rounded-lg overflow-hidden">
          <thead className="bg-background-100">
            <tr>
              <th className="text-left px-4 py-3 font-semibold text-foreground-900">Method</th>
              <th className="text-left px-4 py-3 font-semibold text-foreground-900">Speed</th>
              <th className="text-left px-4 py-3 font-semibold text-foreground-900">Best For</th>
              <th className="text-left px-4 py-3 font-semibold text-foreground-900">Notes</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-background-200">
            <tr className="bg-white">
              <td className="px-4 py-3 font-medium text-foreground-800">EMS</td>
              <td className="px-4 py-3 text-foreground-600">7-21 days</td>
              <td className="px-4 py-3 text-foreground-600">General parcels</td>
              <td className="px-4 py-3 text-foreground-600">Reliable, widely available</td>
            </tr>
            <tr className="bg-background-50">
              <td className="px-4 py-3 font-medium text-foreground-800">DHL</td>
              <td className="px-4 py-3 text-foreground-600">3-10 days</td>
              <td className="px-4 py-3 text-foreground-600">Urgent items</td>
              <td className="px-4 py-3 text-foreground-600">Fast but expensive</td>
            </tr>
            <tr className="bg-white">
              <td className="px-4 py-3 font-medium text-foreground-800">FedEx</td>
              <td className="px-4 py-3 text-foreground-600">5-14 days</td>
              <td className="px-4 py-3 text-foreground-600">Heavy packages</td>
              <td className="px-4 py-3 text-foreground-600">Good for large items</td>
            </tr>
            <tr className="bg-background-50">
              <td className="px-4 py-3 font-medium text-foreground-800">Line Shipping</td>
              <td className="px-4 py-3 text-foreground-600">10-30 days</td>
              <td className="px-4 py-3 text-foreground-600">Budget conscious</td>
              <td className="px-4 py-3 text-foreground-600">Cheapest option, slower</td>
            </tr>
          </tbody>
        </table>
      </div>

      <p>
        To optimize shipping costs, consider consolidating multiple items into a single shipment. Shipping one large package is almost always cheaper than shipping several small ones. The spreadsheet notes which items are lightweight and easy to bundle, helping you plan consolidated orders.
      </p>

      <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mt-10 mb-4">
        Coupons, Discounts, and Saving Money
      </h2>
      <p>
        Smart buyers always look for ways to reduce costs. The AllChinaBuy spreadsheet occasionally includes notes about active coupon codes, seasonal promotions, and referral bonuses. While specific codes change frequently, here are reliable ways to save:
      </p>
      <ul className="list-disc list-inside space-y-2 ml-4">
        <li>Check the main AllChinaBuy website for first-time buyer discounts.</li>
        <li>Look for seasonal sales during major shopping events like Singles Day and Black Friday.</li>
        <li>Use referral codes if you are introducing friends to the platform.</li>
        <li>Compare agent fees across different purchasing agents for the same items.</li>
        <li>Consider group buys with friends to split international shipping costs.</li>
      </ul>

      <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mt-10 mb-4">
        Safety and Common Mistakes to Avoid
      </h2>
      <p>
        Even with a great tool like the AllChinaBuy spreadsheet, buyers can still make costly mistakes. Here are the most common pitfalls and how to avoid them:
      </p>
      <ul className="list-disc list-inside space-y-2 ml-4">
        <li><strong>Skipping QC:</strong> Always request quality control photos, especially for expensive or branded items.</li>
        <li><strong>Underestimating shipping:</strong> International shipping can cost more than the products themselves. Calculate before you buy.</li>
        <li><strong>Ignoring size charts:</strong> Asian sizing often differs from Western sizing. Always check measurements and ask for insole length for shoes.</li>
        <li><strong>Buying from unverified sellers:</strong> Stick to sellers with positive community feedback whenever possible.</li>
        <li><strong>Not declaring customs properly:</strong> Work with your agent to declare accurate values to avoid customs issues.</li>
      </ul>

      <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 mt-10 mb-4">
        Conclusion: Make the AllChinaBuy Spreadsheet Work for You
      </h2>
      <p>
        The <a href="/" className="text-primary-600 font-medium hover:underline">AllChinaBuy spreadsheet</a> is more than just a product list. It is a strategic tool that, when used correctly, can transform your overseas shopping experience from guesswork into a streamlined process. By understanding the categories, verifying sellers, prioritizing quality control, optimizing shipping, and staying alert for discounts, you can shop confidently and efficiently.
      </p>
      <p>
        Ready to start exploring? <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="text-primary-600 font-medium hover:underline">Visit the main AllChinaBuy spreadsheet website</a> to browse the full catalog and begin your shopping journey. For more buying guides and tips, return to our <a href="/" className="text-primary-600 font-medium hover:underline">homepage</a> and explore our growing collection of resources.
      </p>
    </>
  );
}

function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs = [
    {
      question: 'What is the AllChinaBuy spreadsheet?',
      answer: 'The AllChinaBuy spreadsheet is an organized collection of product listings, seller information, and buying guides that helps shoppers discover quality products from Chinese sellers. It includes curated categories, verified seller data, and practical shopping tips to make your buying experience smoother.',
    },
    {
      question: 'Is AllChinaBuy legit and safe to use?',
      answer: 'AllChinaBuy operates as a purchasing agent platform. Like any cross-border shopping service, it requires careful use. Always verify sellers, check QC photos before shipping, and use secure payment methods. Our safety guides provide detailed steps to protect yourself while shopping.',
    },
    {
      question: 'How much does AllChinaBuy shipping cost?',
      answer: 'Shipping costs vary based on your destination country, package weight, and chosen shipping method. Common options include EMS, DHL, FedEx, and various line shipping services. Use the AllChinaBuy shipping calculator on the main website for accurate estimates to your specific location.',
    },
    {
      question: 'Can I use coupon codes on AllChinaBuy?',
      answer: 'Yes, AllChinaBuy frequently offers coupon codes and discount promotions. These may include first-time buyer discounts, seasonal sales, and referral bonuses. Check the main website for current active coupons and follow our spreadsheet for updated discount opportunities.',
    },
    {
      question: 'What is QC and why does it matter?',
      answer: 'QC (Quality Control) is the process of inspecting your products before they are shipped from the warehouse. AllChinaBuy provides QC photos so you can verify the item condition, accuracy, and quality. This step is crucial because it allows you to address issues before international shipping, saving time and money on returns.',
    },
  ];

  return (
    <div className="bg-white rounded-2xl border border-background-200 p-6 md:p-8">
      <h3 className="text-2xl font-bold text-foreground-950 mb-6">Frequently Asked Questions</h3>
      <div className="space-y-3">
        {faqs.map((faq, idx) => (
          <div key={idx} className="border border-background-200 rounded-lg overflow-hidden">
            <button
              onClick={() => setOpenIndex(openIndex === idx ? null : idx)}
              className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-background-50 transition-colors"
            >
              <span className="font-semibold text-foreground-900 pr-4">{faq.question}</span>
              <i className={`ri-arrow-down-s-line text-lg text-foreground-400 flex-shrink-0 transition-transform ${openIndex === idx ? 'rotate-180' : ''}`} />
            </button>
            {openIndex === idx && (
              <div className="px-5 pb-4 text-foreground-600 leading-relaxed">
                {faq.answer}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}